/* eslint-disable no-useless-escape */
import { Button } from '@mui/material';
import { Form, Formik } from 'formik';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSubmitEnabled, maxValidationNumber, maxValidationString, minValidationNumber, minValidationString, postalCodeErrorMessage, requiredField } from '../../../lib/helperFuncs';
import DialogAction from '../../Dialog/DialogAction';
import { formInStepperBuilder } from '../../form_v2/helpers';
import FormActionButtonLayout from '../../layout/Form/FormActionButtonLayout';
import FormLayout from '../../layout/Form/FormLayout';
import { VerticalStepper } from '../../Stepper';
import * as Yup from 'yup';
import { FieldControllerTypes, FieldTypes, IFormController } from '../../form_v2/types';
import { FO_Status } from '../../../lib/static/offices/selectOptions';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import TheatersIcon from '@mui/icons-material/Theaters';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import { FilmOfficeSteps } from './type';
import { useTranslation } from 'react-i18next';
import { matchIsValidTel } from 'mui-tel-input';
import { getRegions } from '../../../lib/static/locations/fetchers';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AsyncButton from '../../AsyncButton/AsyncButton';

const FilmOfficeStepsComp = (props: FilmOfficeSteps) => {
  const {initialValues, onSubmit, edit=false, isLoading=false} = props;
  
  const navigate = useNavigate();
  const {t} = useTranslation();
  const [openSubmit, setOpenSubmit] = useState<boolean>(false);
  const [openCancel, setOpenCancel] = useState<boolean>(false);
  const [regions, setRegions] = useState<any>([]);
  
  const validationSchema = Yup.object({
    // step_1
    title:
    Yup.string().required(requiredField)
      .min(1, minValidationString(1)).max(100, maxValidationString(1000)),
    // step_2
    address: Yup
      .string().nullable()
      .min(1, minValidationString(1))
      .max(50, maxValidationString(50)),
    address_number: Yup
      .string()
      .min(1, minValidationNumber(1))
      .max(5, maxValidationNumber(5))
      .matches(/^\d+([A-Za-z]|\-\d+)?$/, 'Μη αποδεκτή μορφή Αριθμού Διεύθυνσης'),
    tk: Yup
      .number()
      .required(requiredField)
      .nullable()
      .min(10000, postalCodeErrorMessage())
      .max(99999, postalCodeErrorMessage()),
    // step_3
    email: Yup.string().required(requiredField).email('Μη αποδεκτή μορφή Email')
      .matches(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/, 'Μη αποδεκτή μορφή Email'),
    phone_number: Yup.string()
      .required(requiredField)
      .test('phone_number', 'Παρακαλώ προσθέστε έγκυρη μορφή τηλεφώνου', (value) => matchIsValidTel(value || '')),
    // step_4
    timeFrom: Yup
      .string()
      .nullable(),
    timeTo: Yup
      .string()
      .nullable(),
    // step_5
    regions: Yup.number().required(requiredField)
  });

  const step1Form: IFormController[] = [{
    control: FieldControllerTypes.input,
    label: t('Όνομα Film Office'),
    name: 'title',
    required: true,
    disabled: edit
      ? false //check based on roles
      : false
  }];

  const step2Form: IFormController[][] = [
    [{
      control: FieldControllerTypes.input,
      label: t('Oδός'),
      name: 'address',
      required: true,
      disabled: edit
        ? false //check based on roles
        : false
    },
    {
      control: FieldControllerTypes.input,
      label: t('Αριθμός'),
      name: 'address_number',
      required: true,
      disabled: edit
        ? false //check based on roles
        : false
    },
    {
      control: FieldControllerTypes.input,
      type: FieldTypes.number,
      label: t('ΤΚ'),
      name: 'tk',
      required: true,
      positives_only: true,
      disabled: edit
        ? false //check based on roles
        : false
    }]
  ];

  const step3Form: IFormController[] = [
    {
      control: FieldControllerTypes.input,
      label: 'Ε-mail',
      name: 'email',
      required: true,
    },{
      control: FieldControllerTypes.telInput,
      label: t('Τηλέφωνο'),
      name: 'phone_number',
      required: true,
    },];

  const step4Form: IFormController[][] = [
    [{
      control: FieldControllerTypes.time,
      name: 'timeFrom',
      label: 'Από',
    },
    {
      control: FieldControllerTypes.time,
      name: 'timeTo',
      label: 'Έως',
    }]
  ];

  const step5Form: IFormController[] = [
    {
      control: FieldControllerTypes.autoselect,
      label: t('Περιοχές δικαιοδοσίας'),
      name: 'regions',
      options: regions,
      required: true,
      customLabel: true,
      info: true,
      tooltip: 'Εμφανίζονται μόνο οι διαθέσιμες Περιφέρειες/Δήμοι',
      disabled: edit
        ? false //check based on roles
        : false
    },
    {
      control: FieldControllerTypes.select,
      label: t('Κατάσταση'),
      name: 'is_enabled',
      options: FO_Status,
      disabled: true,
      required: true,
      info: true,
      tooltip: t('Για να μπορεί να δημοσιοποιηθεί ένα γραφείο Film Office στο κοινό θα πρέπει να έχει οριστεί τουλάχιστον ένας Film Office Publisher γι΄αυτό')
    }
  ];

  const step1 = {
    label: t('Μονάδας'),
    icon: <TheatersIcon />,
    content: step1Form
  };
  const step2 = {
    label: t('Στοιχεία Διεύθυνσης'),
    icon: <LocationOnIcon />,
    content: step2Form
  };
  const step3 = {
    label: t('Στοιχεία Επικοινωνίας'),
    icon: <LocalPhoneIcon />,
    content: step3Form
  };
  const step4 = {
    label: t('Ωράριο Λειτουργίας'),
    icon: <AccessTimeIcon />,
    content: step4Form
  };
  const step5 = {
    label: t('Χαρακτηριστικά Οργανικής Μονάδας'),
    icon: <OndemandVideoIcon />,
    content: step5Form
  };

  const wholeForm = [
    { ...step1 },
    { ...step2 },
    { ...step3 },
    { ...step4 },
    { ...step5},

  ];

  const getRegionsParametrics = async () => {
    const regions = await getRegions({available: true});
    setRegions(edit
      ? [...regions, ...[ initialValues.region ]]
      : regions
    );
    
  };
  useEffect(() => {
    getRegionsParametrics();
  }, []);

  return (
    <>
      <Formik
        onSubmit={() => undefined}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={false}
      >
        {formik =>
          (<div>
            <Form>
              <FormLayout>
                <VerticalStepper activeStep={1}>
                  {formInStepperBuilder(
                    wholeForm,
                    formik,
                    edit
                      ? false
                      : true
                  )}
                </VerticalStepper>
              </FormLayout>
              <FormActionButtonLayout>
                <Button variant='outlined' color='error'
                  onClick={() => setOpenCancel(true)}> {t('Ακύρωση')} </Button>
                <AsyncButton
                  title={edit
                    ? t('Αποθήκευση')
                    : t('Υποβολή') 
                  }
                  isLoading={isLoading}
                  onClick={() => setOpenSubmit(true)}
                  disabled={isSubmitEnabled(formik?.errors, formik?.dirty)}
                />
                <DialogAction open={openCancel} handleClose={() => setOpenCancel(false)}
                  title={'Ακύρωση Δημιουργίας Film Office'} content={edit
                    ? 'Είστε σίγουρος/η ότι θέλετε να ακυρώσετε με την επεξεργασία του Film Office;'
                    : 'Ειστε σιγουρος/η ότι θέλετε να ακυρώσετε την επεξεργασία film office;'}
                  onAccept={() => {
                    setOpenCancel(false); navigate(-1);
                  }}/>
                <DialogAction open={openSubmit} handleClose={() => setOpenSubmit(false)}
                  title={edit 
                    ? 'Επιβεβαίωση Επεξεργασίας'
                    : 'Επιβεβαίωση Υποβολής'} 
                  content={edit
                    ? 'Είστε σίγουρος/η ότι θέλετε να προχωρήσετε με την επεξεργασία του Film Office;'
                    : 'Είστε σίγουρος/η ότι θέλετε να προχωρήσετε με την δημιουργία του Film Office;'}
                  onAccept={() => {
                    setOpenSubmit(false);
                    onSubmit(formik.values);
                  }}/>
              </FormActionButtonLayout>
            </Form>
          </div>)
        }
      </Formik>
    </>
  );
};

export default FilmOfficeStepsComp;